import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { sendReservationRequest } from '../services/reservationService';

const Form = styled.form`
  background: #fff;
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
  display: grid;
  gap: 1rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const Input = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  border: 1px solid #d9d2c8;
  border-radius: 10px;
  font: inherit;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 0.9rem 1rem;
  border: 1px solid #d9d2c8;
  border-radius: 10px;
  resize: vertical;
  font: inherit;
`;

const ConsentBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.9rem 1rem;
  border: 1px solid #eadbc5;
  border-radius: 10px;
  background: #fffaf2;
  line-height: 1.5;

  input {
    margin-top: 0.25rem;
  }

  a {
    color: #7a1f2b;
    font-weight: 700;
  }
`;

const SecurityBox = styled.div`
  display: grid;
  gap: 0.6rem;
  padding: 1rem;
  border: 1px solid #eadbc5;
  border-radius: 10px;
  background: #fffaf2;
`;

const SecurityTitle = styled.div`
  font-weight: 700;
  color: #7a1f2b;
`;

const SecurityRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 180px;
  gap: 1rem;
  align-items: center;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const SecurityQuestion = styled.div`
  font-weight: 600;
`;

const HiddenField = styled.div`
  display: none;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #5f5044;
`;

const Button = styled.button`
  background: #6e1f2a;
  color: #fff;
  border: none;
  padding: 0.95rem 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    transform: none;
  }
`;

const Message = styled.p`
  margin-top: 0.5rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  color: ${({ $success }) => ($success ? '#14532d' : '#7f1d1d')};
  background: ${({ $success }) => ($success ? '#dcfce7' : '#fee2e2')};
  border: 1px solid ${({ $success }) => ($success ? '#86efac' : '#fecaca')};
`;

function createSecurityQuestion() {
  const firstNumber = Math.floor(Math.random() * 8) + 2; // 2-9
  const secondNumber = Math.floor(Math.random() * 8) + 2; // 2-9

  return {
    firstNumber,
    secondNumber,
    result: firstNumber + secondNumber
  };
}

function ReservationForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    persons: '',
    message: ''
  });

  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState(createSecurityQuestion);
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [websiteField, setWebsiteField] = useState('');

  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const resetSecurityQuestion = () => {
    setSecurityQuestion(createSecurityQuestion());
    setSecurityAnswer('');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      date: '',
      time: '',
      persons: '',
      message: ''
    });

    setPrivacyAccepted(false);
    resetSecurityQuestion();
    setWebsiteField('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setStatus(null);

    // Honeypot: echte Besucher sehen dieses Feld nicht.
    // Wenn es trotzdem befüllt ist, war es sehr wahrscheinlich ein Bot.
    if (websiteField.trim() !== '') {
      resetForm();
      setStatus({
        success: true,
        message:
          'Vielen Dank! Ihre Reservierungsanfrage wurde erfolgreich gesendet. Wir melden uns zur Bestätigung.'
      });
      return;
    }

    if (!privacyAccepted) {
      setStatus({
        success: false,
        message:
          'Bitte bestätigen Sie zuerst, dass Sie die Datenschutzerklärung gelesen haben.'
      });
      return;
    }

    const normalizedAnswer = Number(String(securityAnswer).trim());

    if (normalizedAnswer !== securityQuestion.result) {
      setStatus({
        success: false,
        message:
          'Die Sicherheitsfrage wurde nicht korrekt beantwortet. Bitte versuchen Sie es erneut.'
      });
      resetSecurityQuestion();
      return;
    }

    setIsSubmitting(true);

    const result = await sendReservationRequest({
      ...formData,
      privacyAccepted: privacyAccepted ? 'Ja' : 'Nein',
      securityQuestion: `${securityQuestion.firstNumber} + ${securityQuestion.secondNumber}`,
      securityAnswer
    });

    setStatus(result);

    if (result.success) {
      resetForm();
    }

    setIsSubmitting(false);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Grid>
        <Input
          type="text"
          name="name"
          placeholder="Name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <Input
          type="tel"
          name="phone"
          placeholder="Telefon"
          value={formData.phone}
          onChange={handleChange}
          required
        />

        <Input
          type="email"
          name="email"
          placeholder="E-Mail"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <Input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          required
        />

        <Input
          type="time"
          name="time"
          value={formData.time}
          onChange={handleChange}
          required
        />

        <Input
          type="number"
          name="persons"
          placeholder="Personen"
          value={formData.persons}
          onChange={handleChange}
          required
          min="1"
        />
      </Grid>

      <Textarea
        name="message"
        placeholder="Nachricht / Wünsche"
        value={formData.message}
        onChange={handleChange}
      />

      <HiddenField>
        <label>
          Website
          <input
            type="text"
            name="website"
            tabIndex="-1"
            autoComplete="off"
            value={websiteField}
            onChange={(e) => setWebsiteField(e.target.value)}
          />
        </label>
      </HiddenField>

      <ConsentBox>
        <input
          type="checkbox"
          checked={privacyAccepted}
          onChange={(e) => setPrivacyAccepted(e.target.checked)}
          required
        />
        <span>
          Ich habe die <Link to="/datenschutz">Datenschutzerklärung</Link> gelesen.
          Meine Angaben werden zur Bearbeitung der Reservierungsanfrage verarbeitet und
          über den Dienst Web3Forms per E-Mail an den Betreiber weitergeleitet.
        </span>
      </ConsentBox>

      <SecurityBox>
        <SecurityTitle>Kurzer Heurigen-Check</SecurityTitle>
        <SecurityRow>
          <SecurityQuestion>
            Bitte lösen Sie die Sicherheitsfrage: {securityQuestion.firstNumber} +{' '}
            {securityQuestion.secondNumber} = ?
          </SecurityQuestion>

          <Input
            type="number"
            inputMode="numeric"
            placeholder="Antwort"
            value={securityAnswer}
            onChange={(e) => setSecurityAnswer(e.target.value)}
            required
          />
        </SecurityRow>
        <Hint>Damit schützen wir das Formular vor automatisierten Spam-Anfragen.</Hint>
      </SecurityBox>

      <Hint>
        Die Reservierung ist erst nach Rückbestätigung durch den Stadtheurigen verbindlich.
      </Hint>

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Reservierung wird gesendet...' : 'Reservierungsanfrage senden'}
      </Button>

      {status && <Message $success={status.success}>{status.message}</Message>}
    </Form>
  );
}

export default ReservationForm;